import { ChartDataset, FontSpec } from "chart.js";
import { ChartPlugins, ChartUtils } from "@app/utils/chartUtils";
import { useTheme } from "@mui/material";
import {
  CarPortfolioHeatmapChartDataset,
  CarPortfolioHeatmapChartItem,
} from "@app/pages/assetAllocation/asset-allocation-types";
import { SimulatedForwardProbabilitiesACData } from "../useSimulatedForwardProbabilitiesACData";
import { CarPdfBox, CarPdfBoxProps } from "@app/components/PdfBox";
import { CarPdfChart, CarPdfChartProps } from "@app/components/PdfChart";
import { SimulatedForwardProbabilitiesData } from "../useChartSimulatedForwardProbabilitiesData";

interface InternalPdfChartProps
  extends Omit<CarPdfChartProps, "options" | "data" | "plugins" | "type"> {
  label: string;
  datasets: CarPortfolioHeatmapChartDataset[];
  isDownside?: boolean;
  yLabelPosition: "right" | "left";
  isWhiteContext?: boolean;
}

const InternalPdfChart = ({
  label,
  datasets,
  isDownside,
  yLabelPosition,
  isWhiteContext,
  ...props
}: InternalPdfChartProps) => {
  const theme = useTheme();

  const titleFont: Partial<FontSpec> = {
    size: 10,
    weight: 600,
    lineHeight: 1,
    family: theme.typography.fontFamily,
  };

  const font: Partial<FontSpec> = {
    size: 10,
    weight: 400,
    lineHeight: 1,
    family: theme.typography.fontFamily,
  };

  return (
    <CarPdfChart
      type="line"
      options={{
        scales: {
          x: {
            display: true,
            title: {
              display: true,
              text: "Year",
              font,
              color: theme.palette.softBlack,
              padding: 4,
            },
            grid: ChartUtils.getGridOptions({ theme }),
            ticks: {
              font,
              color: theme.palette.softBlack,
              maxRotation: 0,
              padding: 0,
            },
          },
          y: {
            position: yLabelPosition,
            ticks: {
              font,
              color: theme.palette.softBlack,
              padding: 0,
            },
            grid: ChartUtils.getGridOptions({ theme }),
            title: {
              display: true,
              text: "Value ($)",
              color: theme.palette.softBlack,
              font,
              padding: 4,
            },
            max: isDownside ? 100 : undefined,
          },
        },
        plugins: {
          title: {
            display: true,
            align: "start",
            fullSize: false,
            text: label,
            font: titleFont,
            color: theme.palette.softBlack,
            padding: 3,
          },

          roundedBackground: ChartUtils.getRoundedBackgroundOptions({
            theme,
            isWhiteContext,
          }),
        },
      }}
      data={{
        labels: datasets[0]?.x,
        datasets: datasets.map<ChartDataset<"line", number[]>>((ds) => ({
          label: ds.label,
          data: ds.y,
          borderColor: ds.color,
          backgroundColor: ds.color,
          fill: ds.fill,
          pointRadius: 0,
          borderWidth: 2,
        })),
      }}
      plugins={[ChartPlugins.roundedBackground]}
      {...props}
    />
  );
};

interface SimulatedForwardProbabilitiesPdfChartProps
  extends Omit<CarPdfBoxProps, "children"> {
  data: SimulatedForwardProbabilitiesData;
}

export const SimulatedForwardProbabilitiesPdfChart = ({
  data,
  style,
  ...props
}: SimulatedForwardProbabilitiesPdfChartProps) => {
  return (
    <CarPdfBox style={{ ...style }} {...props}>
      <CarPdfBox style={{ flexDirection: "row" }}>
        <InternalPdfChart
          isWhiteContext
          label={data.label1}
          datasets={data.datasets1}
          width={data.isCompare ? 270 : 540}
          height={210}
          yLabelPosition="left"
        />
        {data.isCompare && (
          <InternalPdfChart
            style={{ marginLeft: 5 }}
            isWhiteContext
            label={data.label2}
            datasets={data.datasets2}
            width={270}
            height={210}
            yLabelPosition="left"
          />
        )}
      </CarPdfBox>
      <CarPdfBox style={{ flexDirection: "row", marginTop: 20 }}>
        <InternalPdfChart
          isWhiteContext
          label={data.label1Downside}
          datasets={data.datasets1}
          width={data.isCompare ? 270 : 540}
          height={210}
          isDownside
          yLabelPosition="left"
        />
        {data.isCompare && (
          <InternalPdfChart
            style={{ marginLeft: 5 }}
            isWhiteContext
            label={data.label2Downside}
            datasets={data.datasets2}
            width={270}
            height={210}
            isDownside
            yLabelPosition="left"
          />
        )}
      </CarPdfBox>
    </CarPdfBox>
  );
};

interface SimulatedForwardProbabilitiesPdfChartACProps
  extends Omit<CarPdfBoxProps, "children"> {
  data: SimulatedForwardProbabilitiesACData;
}

export const SimulatedForwardProbabilitiesPdfChartAC = ({
  data,
  style,
  ...props
}: SimulatedForwardProbabilitiesPdfChartACProps) => {
  return (
    <CarPdfBox style={{ ...style }} {...props}>
      <CarPdfBox style={{ flexDirection: "row" }}>
        <InternalPdfChart
          isWhiteContext
          label={data.label1}
          datasets={data.datasets1}
          width={270}
          height={210}
          yLabelPosition="left"
        />
        <InternalPdfChart
          style={{ marginLeft: 5 }}
          isWhiteContext
          label={data.label2}
          datasets={data.datasets2}
          width={270}
          height={210}
          yLabelPosition="left"
        />
      </CarPdfBox>
      <CarPdfBox style={{ flexDirection: "row", marginTop: 20 }}>
        <InternalPdfChart
          isWhiteContext
          label={data.label1Downside}
          datasets={data.datasets1}
          width={270}
          height={210}
          isDownside
          yLabelPosition="left"
        />
        <InternalPdfChart
          style={{ marginLeft: 5 }}
          isWhiteContext
          label={data.label2Downside}
          datasets={data.datasets2}
          width={270}
          height={210}
          isDownside
          yLabelPosition="left"
        />
      </CarPdfBox>
    </CarPdfBox>
  );
};
